import React, {useState} from 'react';
import {IAdditionalData, IDirectImportItem, IInstalledExtension} from "../../models/Extension";
import {Checkbox, ComboBox, Label, TextField} from "@fluentui/react";
import FileUpload from "./FileUpload";
import LibrarySelection from "./LibrarySelection";
import ExtensionFieldSearch from "./ExtensionFieldSearch";
import {useSelector} from "react-redux";
import {RootState} from "../../store/rootReducer";
import ExtensionSearchResults from "./ExtensionSearchResults";
import ExtensionDirectImportPreview from "./ExtensionDirectImportPreview";
import ExtensionDependentOption from "../ExtensionInstallation/ExtensionDependentOption";
import {useGetWorkspaceQuery} from "../../store/Api";
import {useLazyGetStorefrontsQuery} from "../../store/endpoints/StorefrontSettingsEndpoints";


interface IExtensionFieldProps {
    field: IAdditionalData;
    sourceFieldValue?: string
    extension: IInstalledExtension;
    isDisabled?: boolean;
    isLoading?: boolean;
    value?: string;
    onChange: (value: string | boolean | undefined) => void;
    onExportSelection?: (value: IDirectImportItem[]) => void;
    workspaceId: string;
    /** Header element the results grid portals its paginator into (panel mode). */
    paginatorHost?: HTMLElement | null;
}

const ExtensionField: React.FC<IExtensionFieldProps> = (props) => {
    const targetExtension = useSelector((state: RootState) => state.settings.targetExtension);
    const targetExtensionMappings = useSelector((state: RootState) => state.settings.mappedExtensionFields);
    const [value, setValue] = useState<string | undefined>(undefined);
    const [storefrontOptions, setStorefrontOptions] = useState<{key: string, text: string}[]>([]);
    const [storefronts] = useLazyGetStorefrontsQuery()

    React.useEffect(() => {
        if (props.field.fieldType === 'storefrontOption' && props.extension.id) {
            storefronts().unwrap().then((result) => {
                if (result?.items) {
                    const options = result.items.map(
                        (val: any) => ({key: val.id, text: val.name})
                    )
                    setStorefrontOptions(options)
                }
            })
        }
    }, [props.field.fieldType, props.extension.id])
    React.useEffect(() => {
        if (targetExtension && targetExtensionMappings) {
            const mappedField = targetExtensionMappings[props.field.field];
            if (mappedField) {
                setValue(() => mappedField);
            }
        } else if (props.value && props.value != value) {
            setValue(() => props.value);
        } else if (!props.value && props.field.defaultValue){
            props.onChange(props.field.defaultValue);
        }
    }, [props.value, targetExtension, targetExtensionMappings]);

    const change = (value: string | undefined) => {
        props.onChange(value);
    }
    

    const changeDependentOption = (field: string, value: string | undefined) => {
        props.onChange(value);
        setValue(value)
    }

    if (props.field.fieldType === 'static') return null;
    
    if (props.field.fieldType == 'storefrontOption') {
        return (<div>
            {storefrontOptions && storefrontOptions.length > 0 && <ComboBox
                label={'Storefront'}
                options={storefrontOptions}
                autoComplete={"on"}
                disabled={!storefrontOptions}
                placeholder={'Select Storefront'}
                selectedKey={value}
                onChange={(e, d) => {
                    change(d?.key.toString() ?? '')
                }}/>}
        </div>)
    }
    
    if(props.field.fieldType === 'user' || props.field.fieldType === 'search' || props.field.fieldType === 'defaultedOption') {
        if (props.field.searchable) {
            return (
                <div>
                    <ExtensionFieldSearch
                        workspaceId={props.workspaceId}
                        isDisabled={props.isDisabled}
                        field={props.field}
                        extensionId={props.extension.id}
                        initialValue={value}
                        onChange={v => change(v)}
                    />
                    <br/>
                </div>
            )
        }
        else if(props.field.hasOptions){
            return(
                <ExtensionDependentOption
                    initialValue={value ?? ''}
                    optionsField={props.field}
                    extensionId={props.extension.id}
                    onChange={changeDependentOption}
                />)
        }
        return (
            <TextField
                label={props.field.description}
                id={props.field.field}
                onBlur={(e) => change(e.target.value)}
                onChange={(e, v) => setValue(() => v)}
                value={value}
                disabled={props.isDisabled}
                placeholder={props?.isLoading ? 'Loading...' : ''}
            />
        )
    }
    
    if (props.field.fieldType === 'defaultedText') {
        return (
            <TextField
                label={props.field.description}
                id={props.field.field}
                onBlur={(e) => change(e.target.value)}
                onChange={(e, v) => setValue(() => v)}
                value={value}
                disabled={props.isDisabled}
                placeholder={props?.isLoading ? 'Loading...' : ''}
            />
        )
    }

    if (props.field.fieldType === 'flag') {
        return (
            <div key={`extension-flag-${props.field.field}`}>
                <Label>{props.field.description}</Label>
                <Checkbox checked={value == "true"}
                          label={props.field.description}
                          onChange={(e, d) => change(d != undefined ? d.toString() : "")}
                />
            </div>
        );
    }

    if (props.field.fieldType === 'results' && props.sourceFieldValue ) {
        return (
            <div key={`extension-results-${props.field.field}`}>
                <Label>{props.field.description}</Label>
                <ExtensionSearchResults
                    workspaceId={props.workspaceId}
                    extensionId={props.extension.id}
                    crmFolioId={(props.extension.isSubExtension || props.extension.id?.startsWith('folio_')) ? props.extension.source : undefined}
                    extensionName={props.extension.name}
                    paginatorHost={props.paginatorHost}
                    initialValue={props.sourceFieldValue}
                    isDisabled={props.sourceFieldValue == ''}
                    field={props.field}
                    onSelectOption={(selection) => {
                        change(selection)
                    }
                }/>
            </div>
        );
    }

    if (props.field.fieldType === 'directImportPreview' ) {

        return (
            <div key={`extension-directImportPreview-${props.field.field}`}>
                <Label>{props.field.description}</Label>
                <ExtensionDirectImportPreview
                    fieldName={props.field.field}
                    extensionId={props.extension.id}
                    importObjectId={props.sourceFieldValue}
                    sourceName={props.extension.source}
                    onSelectOption={(selection) => {
                        if(props.onExportSelection){
                            props.onExportSelection(selection)
                        }
                    }
                    }/>
            </div>
        );
    }

    if (props.field.fieldType === 'file') {
        return (
            <div key={`extension-upload-${props.field.field}`}>
                <Label>{props.field.description}</Label>
                <FileUpload
                    extensionId={props.extension.id}
                    onSuccess={change}
                />
            </div>
        );
    }

    if (props.field.fieldType === 'library') {
        return <LibrarySelection
            label={props.field.description}
            installedExtension={props.extension}
            onChange={change}
            value={value}
        />

    }
    return null;
}

export default ExtensionField;